<script setup lang="ts">
import { computed } from 'vue'
import type { DailyPanelProps } from '@/ui/daily-panel'
import { expChartCopy } from '@/i18n/exp-chart'
import { widgetCopy, widgetText } from '@/i18n/widget-copy'
import ExpRateChart from '../ExpRateChart.vue'
const props = defineProps<Pick<DailyPanelProps, 'data' | 'locale'>>()
const empty = computed(() => {
  const { data, locale } = props
  const recent = data.expChart?.recent
  const copy = widgetCopy(locale, {
    idle: 'Waiting to start', warming: 'Gathering samples', pending: 'Confirming', paused: 'Paused',
    unavailable: 'Waiting for valid data', ended: 'No chart data', ready: 'Waiting for a sample point',
  }, {
    idle: '等待开始记录', warming: '正在采样', pending: '正在确认', paused: '已暂停',
    unavailable: '等待有效数据', ended: '暂无曲线数据', ready: '等待采样点',
  })
  const state = data.status === 'ended' ? 'ended'
    : data.status === 'starting' ? 'pending'
      : data.status === 'idle' ? 'idle'
        : data.status === 'paused' ? 'paused'
          : data.status === 'interrupted' ? 'unavailable'
            : recent?.status === 'stale' ? 'unavailable' : recent?.status ?? 'unavailable'
  const continuous = recent?.continuousMs
  const windowMs = recent?.windowMs
  const samples = state === 'warming' && continuous != null && windowMs != null
    && Number.isFinite(continuous) && Number.isFinite(windowMs) && continuous >= 0 && windowMs > 0
    ? widgetText(locale, '{seconds}/{window}s', '{seconds}/{window} 秒', {
      seconds: Math.floor(Math.min(continuous, windowMs) / 1000), window: windowMs / 1000,
    }).replace('/', ' / ') : ''
  return { label: copy[state], samples, description: `${expChartCopy[locale].title} · ${copy[state]}${samples ? ` · ${samples}` : ''}` }
})
</script>
<template>
  <div class="daily-chart order-chart">
    <ExpRateChart widget :show-readout="false" :model="data.expChart" :locale="locale" :rate-unit="data.rateUnit">
      <template #empty>
        <div class="daily-chart-empty" :title="empty.description" :aria-label="empty.description" role="img" tabindex="0">
          <span class="daily-chart-empty-heading">{{ expChartCopy[locale].title }}</span>
          <span class="daily-chart-empty-status"><span>{{ empty.label }}</span><span v-if="empty.samples" class="daily-chart-empty-samples">{{ empty.samples }}</span></span>
        </div>
      </template>
    </ExpRateChart>
  </div>
</template>
